import React, { lazy, Suspense, useCallback } from 'react';
import { Row, Table } from 'antd';
import { useSelector } from 'react-redux';
import parse from 'html-react-parser';
import {
  isEmpty as _isEmpty,
  map as _map,
  find as _find,
  forEach as _forEach,
  isString as _isString,
  isUndefined as _isUndefined,
  includes as _includes,
  intersectionBy as _intersectionBy,
  compact as _compact,
  sortBy as _sortBy,
  value as _value,
  omit as _omit
} from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateUniqueName, getGraasMetricDefinition, tooltipNAMapper } from 'utilities/utilities_exports';
import './GraasTable.scss';

const ChartTreeTable = lazy(() =>
  import(/* webpackChunkName: 'ChartTreeTable' */ '../ChartWrapper/Chart/ChartTreeTable')
);
const GraasImage = lazy(() => import(/* webpackChunkName: 'GraasImage' */ 'components/Atoms/GraasImage'));
const TooltipWrapper = lazy(() =>
  import(/* webpackChunkName: 'TooltipWrapper' */ 'components/Atoms/TooltipWrapper/TooltipWrapper')
);
const SMResult = lazy(() => import(/* webpackChunkName: 'SMResult' */ 'components/SMResult/SMResult'));
const tableHeightMap = { 2: 120, 3: 150 };

const GraasTable = props => {
  const {
    data,
    heading,
    defaultPageCount,
    showSummaryBottom,
    isPagingEnabled,
    chartHeight,
    showSummary,
    isCompareEnable,
    isFullScreen,
    dateWiseGranularity,
    tableTreeProps,
    selectedColumn,
    widgetHeight,
    enableInsightsIcon = null
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const merchantData = useSelector(state => state?.User?.accountDetails);
  const currencySymbol = merchantData?.currencySymbol || merchantData?.currencyCode;
  const metricDefinition = useSelector(state => state?.MetricDefinition?.metricDefinition);

  const getUpdatedColumnList = (mainList, managedList) => {
    return (
      _intersectionBy(managedList || [], mainList || [], 'key')
        .map(({ key, position }) => {
          // Find the matching column from the main list
          const column = _find(mainList || [], { key });
          return column ? { ...column, position } : null; // Add position for sorting
        })
        .filter(Boolean) // Remove any null values for columns not in the main list
        .sort((a, b) => a.position - b.position) // Sort by the position defined in managedList
        .map(column => _omit(column, 'position')) || []
    );
  };

  let headCellsCopy = !_isEmpty(selectedColumn)
    ? getUpdatedColumnList([...data?.columns], selectedColumn)
    : [...(data?.columns || [])];

  const TableFooter = () => {
    if (showSummaryBottom) {
      return (
        <Table.Summary.Row style={{ backgroundColor: '#f2f4f7' }}>
          {_map(headCells, (column, index) => (
            <Table.Summary.Cell index={index} align={getColumnAlignment(column?.key)}>
              <span
                className='c-heading sm mb-0 bold '
                style={{
                  marginLeft: '-10px',
                  ...(index !== 0 &&
                    data?.meta_data?.summary?.[headCells[0].key] === 'Target Summary' && {
                      textAlign: 'right'
                    })
                }}
              >
                {data?.meta_data?.summary?.[column.key]}
              </span>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      );
    }
  };

  // !-- render image zoom component
  const renderImageComponent = (data, columnConfig) => {
    if (!_isEmpty(data) && columnConfig?.key === 'PRODUCT_IMAGE') {
      return (
        <Suspense fallback={<></>}>
          <GraasImage
            isShowZoom
            isRounded
            thumbnailPath={data?.substring(10, data.length - 2)}
            zoomImagePath={data?.substring(10, data.length - 2)?.replace('thumbnail/', '')}
          />
        </Suspense>
      );
    }
    return parse(data);
  };

  // Alignment logic with integrated sorting rules
  const getColumnAlignment = column => {
    let data_source = data?.data_source?.[0];
    const columnKeyActual = data_source?.[column + '_actual_value'];
    const columnKey = data_source?.[column];

    // If the '_actual_value' exists and is a number, align right
    if ((columnKeyActual || columnKeyActual === 0) && typeof columnKeyActual === 'number') {
      return 'right';
    }

    // If the column contains a currency symbol, align right
    if (columnKey?.toString().includes(currencySymbol)) {
      return 'right';
    }

    // If the column is a string, align left
    if (_isString(columnKey)) {
      return 'left';
    }

    // Fallback: Align right if it's a number, otherwise left
    return typeof columnKey === 'number' ? 'right' : 'left';
  };

  const getText = text => {
    return text !== 0 && (text === null || _isEmpty(text)) ? 'NA' : text;
  };

  // Function to dynamically calculate column width based on content
  const calculateWidth = (column, records, padding = 20, index) => {
    let data_source = data?.data_source?.[0];
    const columnKey = data_source?.[column?.key];
    const columnKeyActual = data_source?.[column?.key + '_actual_value'];

    const maxLength = records.reduce(
      (max, record) => Math.max(max, String(record[column?.key]).length),
      column?.title?.length // use header length as base
    );
    padding = maxLength < 10 ? padding : 0;
    if (typeof column?.title === 'string' && column?.title?.includes('%')) {
      padding = padding + 10;
    }

    // If the '_actual_value' exists and is a number, return number format width
    if ((columnKeyActual || columnKeyActual === 0) && typeof columnKeyActual === 'number') {
      return maxLength > 20 ? '160px' : maxLength * 10 + padding;
    }

    // If the column contains a currency symbol, return number format width
    if (columnKey?.toString().includes(currencySymbol)) {
      return maxLength > 20 ? '160px' : maxLength * 10 + padding;
    }

    // If the column is a string, return string format width
    if (_isString(columnKey)) {
      return maxLength > 15 ? '120px' : maxLength * 10 + padding;
    }

    return typeof columnKey === 'number' ? (maxLength > 20 ? '160px' : maxLength * 10 + padding) : '120px';
  };

  // TODO - Updated header array with sorter function.
  let headCells = [];
  _forEach(headCellsCopy, (column, index) => {
    headCells.push({
      ...column,
      width: column?.width || calculateWidth(column, data?.data_source),
      align: column?.align || getColumnAlignment(column?.key),
      ...(index === 0 && { fixed: 'left' }),
      ...(index > 0 &&
        !isCompareEnable && {
          sorter: (a, b) => {
            if (
              (a[column?.key + '_actual_value'] || a[column?.key + '_actual_value'] === 0) &&
              (b[column?.key + '_actual_value'] || b[column?.key + '_actual_value'] === 0)
            ) {
              return a[column?.key + '_actual_value'] - b[column?.key + '_actual_value'] || 0;
            } else if (
              a[column?.key]?.toString().includes(currencySymbol) &&
              b[column?.key]?.includes(currencySymbol)
            ) {
              return a[column?.key].replace(currencySymbol, '') - b[column?.key].replace(currencySymbol, '') || 0;
            } else if (_isString(a[column?.key])) {
              return a[column?.key].localeCompare(b[column?.key]);
            } else {
              return a[column?.key] - b[column?.key] || 0;
            }
          }
        }),
      ...(showSummary &&
        !showSummaryBottom &&
        !_isEmpty(data.meta_data.summary) && {
          children: [
            {
              title: _isString(data?.meta_data?.summary?.[column.key]) ? (
                <span className='c-heading sm mb-0 bold'>{parse(data?.meta_data?.summary?.[column.key])} </span>
              ) : (
                <span className='c-heading sm mb-0 bold'>{data?.meta_data?.summary?.[column.key]}</span>
              ),
              dataIndex: column?.key,
              key: column?.key,
              render: text => (
                <span className='c-heading sm mb-0 dont-break'>
                  {index === 0 ? text : _isString(text) ? renderImageComponent(text, column) : text}
                </span>
              )
            }
          ]
        }),
      render: text => (
        <TooltipWrapper
          overlayClassName='graas_custom_toolTip'
          isShowTooltip={tooltipNAMapper[text]}
          title={t(tooltipNAMapper[text])}
        >
          <span className='c-heading sm mb-0 dont-break'>
            {index === 0 ? getText(text) : _isString(text) ? renderImageComponent(text, column) : getText(text)}
          </span>
        </TooltipWrapper>
      ),
      title: () => (
        <>
          <TooltipWrapper
            overlayClassName='graas_custom_toolTip'
            isShowTooltip={getGraasMetricDefinition(metricDefinition, column?.title)}
            title={
              <span qa-data={generateUniqueName(column?.title, 'fcChart-')}>
                <span className='c-heading smd color-white mb-1 mt-10'>{column?.title}</span>
                <span className='c-heading sm dont-break color-white mb-1'>
                  {getGraasMetricDefinition(metricDefinition, column?.title)}
                </span>
                <Row justify='end'>
                  <button
                    className='c-button is-link color-white no-pad mb-1'
                    onClick={() => navigate(`/analyze/deep-dive/metricDefinition/?metric=${column?.title}`)}
                  >
                    {t('GRAAS_LEARN_MORE_GRAAS')}
                  </button>
                </Row>
              </span>
            }
          >
            {column?.title}
          </TooltipWrapper>
        </>
      )
    });
  });

  let pageCount = defaultPageCount !== undefined ? defaultPageCount : 5;
  let pagination = { defaultCurrent: 1, defaultPageSize: !_isUndefined(chartHeight) ? 5 : pageCount };

  if (isPagingEnabled) {
    pagination.total = data?.data_source.length;
    pagination.showQuickJumper = true;
    pagination.showSizeChanger = true;
  } else {
    pagination.showQuickJumper = false;
    pagination.showSizeChanger = false;
  }

  if (dateWiseGranularity === 'tableTree') {
    return (
      <ChartTreeTable
        headCells={[
          ..._map(tableTreeProps?.fullQueryDimensions, (dimension, index) => {
            return {
              dataIndex: dimension,
              key: dimension,
              width: index === 0 ? '240px' : '120px',
              title:
                _find(merchantData?.customCategory || [], Category => Category?.id === dimension)?.value ||
                t('GRAAS_' + dimension + '_GRAAS'),
              render: text => (
                <TooltipWrapper
                  isShowTooltip={tooltipNAMapper[text]}
                  title={tooltipNAMapper[text]}
                  overlayClassName='graas_custom_toolTip'
                >
                  <span className='c-heading sm inline-block dont-break mb-0'>{text}</span>
                </TooltipWrapper>
              )
            };
          }),
          ...headCells.slice(1)
        ]}
        {...tableTreeProps}
        data={data}
        chartHeight={chartHeight}
        heading={heading}
        needToLoadApi={false}
        canExpandable={true}
        widgetHeight={widgetHeight}
      />
    );
  }

  const calculateScrollHeight = () => {
    if (isFullScreen && data?.data_source?.length > 5) {
      return 480;
    } else if (data?.data_source?.length >= 2 && enableInsightsIcon !== null && enableInsightsIcon()) {
      return 200;
    } else if (data?.data_source?.length >= 2) {
      return !_isUndefined(widgetHeight) ? widgetHeight * tableHeightMap[widgetHeight] : 215;
    }
    return undefined;
  };

  // --- Check row col empty
  if (_isEmpty(data?.columns)) return <SMResult type='empty' message={t('GRAAS_NO_DATA_AVAILABLE_GRAAS')} />;

  return (
    <div
      className='Graas-customTable'
      style={heading === t('GRAAS_TOP_CHANNELS_BY_UNRECONCILED_REVENUE_GRAAS') ? { minHeight: '354px' } : {}}
    >
      <Table
        showSorterTooltip={false}
        columns={headCells}
        dataSource={data?.data_source}
        pagination={isPagingEnabled ? { ...pagination } : false}
        className={`${isFullScreen ? 'fullScreen' : ''} ${!_isUndefined(chartHeight) ? 'customSmallTable' : ''}`}
        scroll={{
          x: 'scroll',
          y: calculateScrollHeight()
        }}
        locale={{
          emptyText: <SMResult type='empty' message={t('GRAAS_NO_DATA_AVAILABLE_GRAAS')} />
        }}
        summary={() =>
          showSummary && (
            <Table.Summary fixed>
              <TableFooter />
            </Table.Summary>
          )
        }
        {...props}
      />
    </div>
  );
};

export default GraasTable;
